import React, { useState, useRef, useEffect } from "react";
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, Button, useDisclosure, Image, Box, Flex } from "@chakra-ui/react";
import SignaturePad from "react-signature-canvas";
import { API_URL_BASE } from "services/surveyService";

// Dimensiones fijas de salida para todas las firmas guardadas
const SIGNATURE_OUTPUT_WIDTH = 600;
const SIGNATURE_OUTPUT_HEIGHT = 200;

function SignatureInput({ onChange, value }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [imageURL, setImageURL] = useState(value);
  const sigCanvas = useRef({});
  const objectUrlRef = useRef(null);

  // Sincronizar la imagen mostrada cuando cambia el valor externo
  useEffect(() => {
    // Limpiar URL previa si existe
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    if (!value) {
      setImageURL(null);
      return;
    }

    // Si es string, puede ser dataURL, absoluta o relativa
    if (typeof value === 'string') {
      if (value.startsWith('http') || value.startsWith('data:')) {
        setImageURL(value);
      } else {
        // Construir URL absoluta desde ruta relativa
        const fullUrl = `${API_URL_BASE}${value}`;
        setImageURL(fullUrl);
      }
      return;
    }

    // Si es FileList, crear object URL para previsualización
    if (value instanceof FileList && value.length > 0) {
      const file = value[0];
      const objUrl = URL.createObjectURL(file);
      objectUrlRef.current = objUrl;
      setImageURL(objUrl);
      return;
    }
  }, [value]);

  // Sincronizar atributos width/height del canvas con su tamaño CSS real al abrir el modal.
  // Sin esto, el canvas usa el buffer por defecto (300×150) aunque CSS lo muestre en pantalla completa,
  // lo que produce imágenes distorsionadas y de tamaño incorrecto.
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      if (!sigCanvas.current) return;
      const canvas = sigCanvas.current.getCanvas();
      const ratio = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d").scale(ratio, ratio);
      sigCanvas.current.clear();
    }, 50);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const save = () => {
    const trimmed = sigCanvas.current.getTrimmedCanvas();

    // Normalizar a dimensiones fijas para que todas las firmas tengan el mismo tamaño
    const normalized = document.createElement("canvas");
    normalized.width = SIGNATURE_OUTPUT_WIDTH;
    normalized.height = SIGNATURE_OUTPUT_HEIGHT;
    const ctx = normalized.getContext("2d");

    // Fondo blanco
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, SIGNATURE_OUTPUT_WIDTH, SIGNATURE_OUTPUT_HEIGHT);

    // Escalar la firma recortada para que quepa centrada con padding
    const padding = 20;
    const maxW = SIGNATURE_OUTPUT_WIDTH - padding * 2;
    const maxH = SIGNATURE_OUTPUT_HEIGHT - padding * 2;
    const ratio = Math.min(maxW / trimmed.width, maxH / trimmed.height);
    const drawW = trimmed.width * ratio;
    const drawH = trimmed.height * ratio;
    const offsetX = (SIGNATURE_OUTPUT_WIDTH - drawW) / 2;
    const offsetY = (SIGNATURE_OUTPUT_HEIGHT - drawH) / 2;
    ctx.drawImage(trimmed, offsetX, offsetY, drawW, drawH);

    const dataURL = normalized.toDataURL("image/png");

    fetch(dataURL)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], "signature.png", { type: "image/png" });
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        const fileList = dataTransfer.files;

        setImageURL(dataURL);
        onChange(fileList);
        onClose();
      });
  };

  const handleClear = () => {
    clearSignature();
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setImageURL(null);
    onChange(null);
  };

  return (
    <Box>

      <Modal isOpen={isOpen} onClose={onClose} size={'full'}>
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
          <Flex alignItems="center" justifyContent="center" w="100%" h="90vh">
            <SignaturePad
              ref={sigCanvas}
              canvasProps={{
                className: "signatureCanvas"
              }}
            />
          </Flex>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={save}>
              Guardar
            </Button>
            <Button variant="ghost" onClick={clearSignature}>Limpiar</Button>
            <Button variant="ghost" onClick={onClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      

      {imageURL && (
        <>
          <Image
            src={imageURL}
            alt="signature"
            style={{
              display: "block",
              margin: "0 auto",
              width: "250px",
            }}
          />
          <Button onClick={handleClear} mt={2} mr={2}>Borrar</Button>
        </>
      )}
      <Button onClick={onOpen} mt={2}>
        {imageURL ? "Cambiar" : "Firmar"}
      </Button>

    </Box>
  );
}

export default SignatureInput;